/**
 * MooringLine.ts — elastic line between a boat cleat and a mooring anchor.
 *
 * The anchor is any `MooringAnchor` (buoy, dock bollard, future quay cleat).
 * Models a docking/mooring rope as a unilateral spring + damper:
 *   • Slack (distance ≤ restLength): zero force.
 *   • Taut  (distance >  restLength): tension grows linearly with stretch
 *     and is damped by the rate-of-stretch.
 *
 * Tension is applied as equal-and-opposite forces on the two endpoints:
 *   • boat: `boat.applyExternalForce(fwx, fwz, pwx, pwz)` at the cleat —
 *     this naturally produces both translation and yaw torque.
 *   • anchor: `anchor.applyExternalForce(fx, fy, fz)` (rigid bollards
 *     ignore; a free buoy gets pulled).
 *
 * The vertical component of tension is intentionally dropped on the boat
 * side — a slack hawser doesn't pull the bow up out of the water — and on
 * the anchor side we pass the full Y component so a dynamic buoy can dunk
 * briefly when the boat drifts away on a wave.
 */

import type { Boat } from "./Boat";
import type { MooringAnchor } from "./Dock";

export interface MooringLineOptions {
  boat: Boat;
  /** Cleat position in BOAT BODY frame [x_fwd, y_up, z_stbd] (m). */
  boatCleatBody: [number, number, number];
  /** Anchor on the other end of the rope. */
  anchor: MooringAnchor;
  /** Resting length of the line (m). Tension only beyond this. */
  restLength: number;
  /** Spring stiffness (N/m). Default 800. */
  kSpring?: number;
  /** Damping (N·s/m). Default 120. */
  kDamper?: number;
  /** Active = applies forces. Idle lines still update endpoint cache. */
  active?: boolean;
}

export class MooringLine {
  readonly boat: Boat;
  anchor: MooringAnchor;
  readonly cleat: [number, number, number];
  restLength: number;
  kSpring: number;
  kDamper: number;
  active: boolean;

  // --- Per-frame cache for renderer ---
  /** World-space cleat position (Y included). */
  worldA: [number, number, number] = [0, 0, 0];
  /** World-space anchor attachment position. */
  worldB: [number, number, number] = [0, 0, 0];
  /** Tension magnitude applied last step (N). 0 = slack. */
  tension = 0;
  /** Distance between endpoints last step (m). */
  distance = 0;

  /** Previous distance — used to estimate stretch rate via finite diff. */
  private prevDistance: number | null = null;

  constructor(opts: MooringLineOptions) {
    this.boat = opts.boat;
    this.anchor = opts.anchor;
    this.cleat = [...opts.boatCleatBody];
    this.restLength = opts.restLength;
    this.kSpring = opts.kSpring ?? 800;
    this.kDamper = opts.kDamper ?? 120;
    this.active = opts.active ?? true;
  }

  setBoatCleatBody(body: readonly [number, number, number]): void {
    this.cleat[0] = body[0];
    this.cleat[1] = body[1];
    this.cleat[2] = body[2];
  }

  /** Compute tension and apply forces to boat + anchor for this dt. */
  step(dt: number): void {
    // World-space endpoints.
    const [ax, az] = this.boat.bodyToWorld(
      this.cleat[0],
      this.cleat[1],
      this.cleat[2],
    );
    const ay = this.boat.posY + this.cleat[1];
    const [bx, by, bz] = this.anchor.getWorldPos();
    this.worldA = [ax, ay, az];
    this.worldB = [bx, by, bz];

    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const d = Math.hypot(dx, dy, dz);
    this.distance = d;

    if (!this.active || d <= 1e-4) {
      this.tension = 0;
      this.prevDistance = d;
      return;
    }

    // Slack rope: no force.
    const stretch = d - this.restLength;
    if (stretch <= 0) {
      this.tension = 0;
      this.prevDistance = d;
      return;
    }

    // Stretch rate (positive = getting longer → damp force resists).
    const rate =
      this.prevDistance == null
        ? 0
        : (d - this.prevDistance) / Math.max(dt, 1e-4);
    this.prevDistance = d;

    // Tension along the line (always positive — rope, not a strut).
    const T = Math.max(0, this.kSpring * stretch + this.kDamper * rate);
    this.tension = T;

    // Unit vector from boat cleat → anchor.
    const ix = dx / d;
    const iy = dy / d;
    const iz = dz / d;

    // Force on boat: pulls toward anchor (+i). XZ only for the boat's planar
    // dynamics; vertical component is dropped (rope can't lift the bow).
    this.boat.applyExternalForce(T * ix, T * iz, ax, az);

    // Force on anchor: opposite (−i). Full 3D so a slack-then-snap event
    // can briefly dunk a free buoy. Rigid bollards ignore.
    this.anchor.applyExternalForce(-T * ix, -T * iy, -T * iz);
  }
}
